import axios from 'axios';
import { BaseApiService } from '@/services/backend/base';
import store from '@/store';

export class PersonalFl44APIService extends BaseApiService {
    constructor() {
        super('personal/fl44');
    }

    async declineSignContract(purchaseId, contractId, payload) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/contracts/${contractId}/sign_decline`), payload);
            await store.dispatch('showSuccessToast', 'Отказ от подписания контракта принят');
            return true;
        } catch (err) {
            this.handleErrors(err);
            return false;
        }
    }

    async declineSendContract(purchaseId, payload) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/decline_send_contract`), payload);
            await store.dispatch('showSuccessToast', 'Отказ от отправки контракта принят');
            return true;
        } catch (err) {
            this.handleErrors(err);
            return false;
        }
    }

    async storeSecondContract(purchaseId, payload) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/store_second_contract`), payload);
            return true;
        } catch (err) {
            this.handleErrors(err);
            return false;
        }
    }

    async getNoticesList(filters) {
        try {
            const response = await axios.get(this.getUrl('notices'), { params: filters });
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getProposalsList(filters) {
        try {
            const response = await axios.get(this.getUrl('proposals'), { params: filters });
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getProposal(id) {
        try {
            const response = await axios.get(this.getUrl(`proposals/${id}`));
            return response.data.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getContractsList(filters) {
        try {
            const response = await axios.get(this.getUrl('contracts'), { params: filters });
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getContractsXlsExport(filters) {
        try {
            return await axios.post(this.getUrl('contracts/xls'), filters, { responseType: 'blob' });
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getDeliverablesByYearXlsExport(filters) {
        try {
            return await axios.post(this.getUrl('contracts/deliverables_by_year_xls'), filters, { responseType: 'blob' });
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getPostReportXlsExport() {
        try {
            return await axios.post(this.getUrl('contracts/post_report_xls'), null, { responseType: 'blob' });
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async publishPurchase(purchaseId, emails) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchases/${purchaseId}/publish`), { emails: emails });
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async sendApprovalRequest(purchase) {
        try {
            if (!purchase.id) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchases/${purchase.id}/notice_approval`));
            await store.dispatch('showSuccessToast', 'Извещение № ' + purchase.purchase_info.reg_number + ' отправлено на согласование в ГРБС / МО');
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async unpublishPurchase(purchaseId, regNumber) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchases/${purchaseId}/unpublish`));
            await store.dispatch('showSuccessToast', 'Извещение № ' + regNumber + ' снято с публикации и доступно для редактирования');
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async prolongPurchase(purchaseId) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchases/${purchaseId}/prolong`));
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async deletePurchase(purchaseId) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            await axios.delete(this.getUrl(`purchases/${purchaseId}`));
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async cancelPurchase(purchaseId, data) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchases/${purchaseId}/cancel`), data);
            await store.dispatch('showSuccessToast', 'Закупка успешно отменена');
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async copyPurchase(purchaseId) {
        try {
            if (!purchaseId) throw Error('Id is not provided');
            const response = await axios.post(this.getUrl(`purchases/${purchaseId}/copy`));
            return response.data.id;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async updateComment(purchaseId, comment) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/comment`), { purchase_comment: comment });
            await store.dispatch('showSuccessToast', 'Комментарий успешно обновлён');
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async storeRating(formData, purchaseId) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/store_rating`), { ...formData });
            await store.dispatch('showSuccessToast', 'Ваша оценка успешно принята');
            return true;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getPurchaseReportsList(filters) {
        try {
            const response = await axios.get(this.getUrl('purchase_reports'), { params: filters });
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getPurchaseReport(reportId) {
        try {
            if (!reportId) throw Error('Id is not provided');
            const response = await axios.get(this.getUrl(`purchase_reports/${reportId}`));
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async storePurchaseReport(reportId, data) {
        try {
            if (!reportId) throw Error('Id is not provided');
            await axios.post(this.getUrl(`purchase_reports/${reportId}`), data);
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async getLimitsList(filters) {
        try {
            const response = await axios.get(this.getUrl('limits'), { params: filters });
            return response.data;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async refreshLimits() {
        try {
            await axios.post(this.getUrl('limits'));
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }

    async updateSupplementaryAgreementLimits(purchaseId, id, payload) {
        try {
            await axios.put(this.getUrl(`purchases/${purchaseId}/limits`), payload);
            await store.dispatch('showSuccessToast', 'Лимиты успешно обновлены');
            return true;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getSupplementaryAgreementRegularTemplateData(id) {
        try {
            const response = await axios.get(this.getUrl(`purchases/${id}/supplementary_agreements/regular_template`));
            return response.data;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getSupplementaryAgreementTerminationTemplateData(id) {
        try {
            const response = await axios.get(this.getUrl(`purchases/${id}/supplementary_agreements/termination_template`));
            return response.data;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async sendSupplementaryAgreementToSupplier(purchaseId, payload) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements`), payload);
            await store.dispatch('showSuccessToast', 'Дополнительное соглашение успешно отправлено поставщику на подписание');
            return true;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getSupplementaryAgreementXML(purchaseId) {
        try {
            const response = await axios.get(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/xml`));
            return response.data;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getTerminationSupplementaryAgreementXML(purchaseId, payload) {
        try {
            const response = await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/termination_xml`), payload);
            return response.data;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async getSupplementaryAgreementDisagreementXML(purchaseId, payload) {
        try {
            const response = await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/disagreement_xml`), payload);
            return response.data;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async storeSignedSupplementaryAgreement(purchaseId, payload, isSupplier = false) {
        try {
            if (isSupplier) {
                await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/sign_supplier`), payload);
                await store.dispatch('showSuccessToast', 'Дополнительное соглашение успешно подписано и отправлено заказчику');
            } else {
                await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/sign_customer`), payload);
                await store.dispatch('showSuccessToast', 'Дополнительное соглашение успешно подписано');
            }
            return true;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async storeCustomerSignedSupplementaryAgreementTermination(purchaseId, payload) {
        try {
            await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/store_termination`), payload);
            await store.dispatch('showSuccessToast', 'Дополнительное соглашение успешно подписано');
            return true;
        } catch (error) {
            this.handleErrors(error);
            throw error;
        }
    }

    async storeSignedSupplementaryAgreementDisagreement(purchaseId, payload, isSupplier = false) {
        try {
            if (isSupplier) {
                await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/disagreement`), { ...payload, is_supplier: isSupplier });
                await store.dispatch('showSuccessToast', 'Протокол разногласий к ДС успешно отправлен заказчику');
            } else {
                await axios.post(this.getUrl(`purchases/${purchaseId}/supplementary_agreements/disagreement`), { ...payload, is_supplier: isSupplier });
                await store.dispatch('showSuccessToast', 'Ответ на протокол разногласий к ДС успешно отправлен поставщику');
            }
            return true;
        } catch (err) {
            this.handleErrors(err);
        }
    }
}
